/**
 * KAP3 math helpers
 * Contract behavior:
 *   - Each Buy Plan generates 7%/month = 0.07/30 ≈ 0.2333%/day, paid per minute
 *   - Withdrawals incur 8% fee → user receives 92% of withdrawn amount
 *   - Each plan caps at 2× the invested amount; then expires
 *   - Min withdraw: $5 USDT, 24h cooldown
 */

const DAILY_RATE = 0.07 / 30; // ≈ 0.002333
const WITHDRAW_FEE = 0.08;
const NET_FACTOR = 1 - WITHDRAW_FEE; // 0.92
const TWO_X_MULTIPLIER = 2;

export const KAP3_DAILY_RATE = DAILY_RATE;
export const KAP3_MONTHLY_RATE = 0.07;
export const KAP3_NET_FACTOR = NET_FACTOR;

/**
 * Compute per-plan analytics from a list of Buy Plan on-chain events.
 * Each plan accrues independently from its own start timestamp.
 */
export const computePlanAnalytics = (buyPlan, nowMs = Date.now()) => {
  const amount = Number(buyPlan.amount_usdt) || 0;
  const start = new Date(buyPlan.date).getTime();
  const elapsedDays = Math.max(0, (nowMs - start) / 86_400_000);

  const target = amount * TWO_X_MULTIPLIER;
  const dailyReward = amount * DAILY_RATE; // gross
  const dailyRewardNet = dailyReward * NET_FACTOR;

  // Theoretical gross accrued (capped at target)
  const grossAccrued = Math.min(elapsedDays * dailyReward, target / NET_FACTOR);
  // Net (what the user actually receives if withdrawing now)
  const netAccrued = grossAccrued * NET_FACTOR;

  // Days remaining until plan reaches 2x (assuming no withdrawals slow it)
  // Actually since plan accrues regardless of withdrawals, we just need:
  //   days_until_target = target_net / dailyRewardNet
  //   where target_net = amount * 2 (the user has to receive amount * 2 net)
  const daysUntil2x = dailyRewardNet > 0 ? target / dailyRewardNet : Infinity;
  const daysRemaining = Math.max(0, daysUntil2x - elapsedDays);

  return {
    amount,
    target,
    dailyReward,           // gross per day
    dailyRewardNet,        // net (92%) per day
    monthlyReward: amount * 0.07,
    monthlyRewardNet: amount * 0.07 * NET_FACTOR,
    elapsedDays,
    daysUntil2x,
    daysRemaining,
    grossAccrued,
    netAccrued,
    progressPct: Math.min(100, (netAccrued / target) * 100),
    isExpired: netAccrued >= target,
  };
};

/**
 * Aggregate analytics across all plans + actual withdrawals.
 * `buyPlans`: array of { amount_usdt, date }
 * `withdrawals`: array of { amount_usdt, date } — these are NET amounts received
 */
export const computeKap3Summary = (buyPlans = [], withdrawals = [], nowMs = Date.now()) => {
  if (buyPlans.length === 0) {
    return {
      totalInvested: 0,
      totalTarget: 0,
      totalWithdrawn: 0,
      pendingNet: 0,
      progressPct: 0,
      dailyRewardNet: 0,
      monthlyRewardNet: 0,
      daysUntilFull2x: 0,
      plans: [],
    };
  }

  const plans = buyPlans.map((bp) => ({
    ...bp,
    analytics: computePlanAnalytics(bp, nowMs),
  }));

  const totalInvested = plans.reduce((s, p) => s + p.analytics.amount, 0);
  const totalTarget = plans.reduce((s, p) => s + p.analytics.target, 0);
  const totalAccruedNet = plans.reduce((s, p) => s + p.analytics.netAccrued, 0);
  const totalWithdrawn = withdrawals.reduce((s, w) => s + (Number(w.amount_usdt) || 0), 0);

  // Pending rewards = accrued − already withdrawn (clamped to >= 0)
  const pendingNet = Math.max(0, totalAccruedNet - totalWithdrawn);

  const dailyRewardNet = plans.reduce(
    (s, p) => s + (p.analytics.isExpired ? 0 : p.analytics.dailyRewardNet),
    0
  );
  const monthlyRewardNet = dailyRewardNet * 30;

  // Days until *the whole portfolio* hits 2x (weighted by remaining)
  const remainingToReach2x = Math.max(0, totalTarget - totalAccruedNet);
  const daysUntilFull2x = dailyRewardNet > 0 ? remainingToReach2x / dailyRewardNet : Infinity;

  return {
    totalInvested,
    totalTarget,
    totalWithdrawn,
    totalAccruedNet,
    pendingNet,
    progressPct: totalTarget > 0 ? (totalWithdrawn / totalTarget) * 100 : 0,
    dailyRewardNet,
    monthlyRewardNet,
    daysUntilFull2x,
    plans,
  };
};

/**
 * "If I buy $X today" calculator
 */
export const projectKap3Purchase = (amount) => {
  const a = Number(amount) || 0;
  const target = a * TWO_X_MULTIPLIER;
  const dailyNet = a * DAILY_RATE * NET_FACTOR;
  const monthlyNet = a * 0.07 * NET_FACTOR;
  const daysToTarget = dailyNet > 0 ? target / dailyNet : 0;
  return {
    invested: a,
    target,
    dailyNet,
    monthlyNet,
    daysToTarget,
    monthsToTarget: daysToTarget / 30,
  };
};
